import React, { useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { useResizeDetection } from '../../../hooks/useResizeDetection';

export default function MobileCarousel({
    children,
    slides = [],
    slideTargetWidth,
    slidesPerPage = 1,
    hasGutters = false,
    className,
    hasPaddingBelow = false,
    // eslint-disable-next-line react/prop-types
    hasNegativeMargins,
    ...rest
}) {
    const [flexBasis, setFlexBasis] = useState(88);
    const maskRef = useRef();
    const isResizing = useResizeDetection(100);
    const items = Array.isArray(children) ? children : slides;

    /**
     * When the windowSize changes, update pages and widths
     */
    useLayoutEffect(() => {
        let newNumPerPage = slidesPerPage;
        const newMaskWidth = maskRef.current.offsetWidth;

        if (slideTargetWidth) {
            // Calculate the number of items that when spaced out in the mask,
            // comes closest to the slideTargetWidth
            const numThatFit = Math.floor(maskRef.current.offsetWidth / slideTargetWidth);

            const remainderAtFit = newMaskWidth % slideTargetWidth;
            const remainderAtFitPlusOne =
                slideTargetWidth * (numThatFit + 1) - newMaskWidth;
            newNumPerPage =
                remainderAtFit < remainderAtFitPlusOne ? numThatFit : numThatFit + 1;
        }

        setFlexBasis(88 / newNumPerPage || 88);
    }, [isResizing, slideTargetWidth, slidesPerPage]);

    // console.log(flexBasis, liFlex);

    return (
        <div className={cx('sarsa-mobile-carousel', className)} ref={maskRef} {...rest}>
            <ul className="sarsa-mobile-carousel-ul">
                <div
                    aria-hidden="true"
                    className={cx('sarsa-mobile-carousel-spacer', {
                        'has-gutters': hasGutters,
                    })}
                />
                {items.map((item, index) => {
                    return (
                        <li
                            key={index}
                            className={cx('sarsa-mobile-carousel-li', {
                                'padding-below': hasPaddingBelow,
                            })}
                            style={{ flex: `0 0 ${flexBasis}%` }}
                        >
                            <div
                                className={cx('sarsa-mobile-carousel-li-inner', {
                                    'has-gutters': hasGutters,
                                })}
                            >
                                <div className="li-item-wrap">{item}</div>
                            </div>
                        </li>
                    );
                })}
                <div
                    aria-hidden="true"
                    className={cx('sarsa-mobile-carousel-spacer', {
                        'has-gutters': hasGutters,
                    })}
                />
            </ul>
        </div>
    );
}

MobileCarousel.propTypes = {
    /** Slides (items) in the carousel. Use this prop OR slides, but NOT both */
    children: PropTypes.array,

    /** Slides (items) in the carousel. Use this prop OR children, but NOT both */
    slides: PropTypes.array,

    /** The approximate width in pixels for each slide. The component
     * will adjust the number of slides per page so that each item most closely
     * matches the desired width. Use this prop OR slidesPerPage, but NOT both  */
    slideTargetWidth: PropTypes.number,

    /** The number of slides to display per page (regardless of the overall
     * width). Use this prop OR slideTargetWidth, but NOT both */
    slidesPerPage: PropTypes.number,

    /** If true, each slide will have a left and right padding of 4px */
    hasGutters: PropTypes.bool,

    /** Adds additional 24px of bottom padding to the carousel <li>. Necessary when
     * displaying card style components.
     */
    hasPaddingBelow: PropTypes.bool,

    /** Additional class added to outer-most container */
    className: PropTypes.string,
};
